import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { User, SubscriptionPlan } from '../../types';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: User | null;
  onSuccess: () => void;
}

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({ isOpen, onClose, client, onSuccess }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      setSelectedPlanId('');
      setPaymentReference('');
      setError('');
    }
  }, [isOpen]);

  const loadPlans = async () => {
    const response = await api.getPlans();
    if (response.success && response.data) {
      setPlans(response.data.filter(p => p.isActive));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client || !selectedPlanId) return;

    setLoading(true);
    setError('');

    const response = await api.changeClientPlan(client.id, selectedPlanId, paymentReference || undefined);

    if (response.success) {
      onSuccess();
      onClose();
    } else {
      setError(response.error || 'Failed to change plan');
    }

    setLoading(false);
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#262626]">
          <h3 className="text-lg font-bold text-white">Change Subscription Plan</h3>
          <p className="text-neutral-500 text-sm">For {client.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-neutral-400 text-sm mb-2">Select Plan</label>
            <select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              required
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">Choose a plan...</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} - ৳{plan.priceBdt.toLocaleString()}/mo ({plan.maxRequestsPerDay} daily)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-neutral-400 text-sm mb-2">Payment Reference (Optional)</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g., BKASH-TXN-123456"
              className="w-full bg-[#1a1a1a] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          {selectedPlanId && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
              <p className="text-emerald-400 text-sm">
                The client's subscription will be updated immediately and will start from today.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-[#1a1a1a] hover:bg-[#262626] text-white rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedPlanId}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl transition-colors"
            >
              {loading ? 'Updating...' : 'Update Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminClients: React.FC = () => {
  const [clients, setClients] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState<User | null>(null);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);

  useEffect(() => {
    loadClients();
  }, [page, statusFilter]);

  const loadClients = async () => {
    setLoading(true);
    const response = await api.getUsers({
      page, pageSize: 10, role: 'client',
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    });
    if (response.success && response.data) {
      setClients(response.data.data);
      setTotalPages(response.data.totalPages);
    }
    setLoading(false);
  };

  const handleApprove = async (id: string) => {
    const response = await api.approveUser(id);
    if (response.success) loadClients();
    else alert(response.error);
  };

  const handleSuspend = async (id: string) => {
    const reason = prompt('Suspension reason:');
    if (!reason) return;
    const response = await api.suspendUser(id, reason);
    if (response.success) loadClients();
    else alert(response.error);
  };

  const handleChangePlan = (client: User) => {
    setSelectedClient(client);
    setShowChangePlanModal(true);
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Client Management</h1>
        <p className="text-neutral-500">Manage all registered clients and their subscriptions</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadClients(); }} className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="w-full bg-[#121212] border border-[#262626] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50"
          />
        </form>
        <div className="flex gap-2">
          {['all', 'active', 'pending', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(1); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                statusFilter === status ? 'bg-emerald-600 text-white' : 'bg-[#1a1a1a] text-neutral-400 hover:bg-[#262626]'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {clients.length > 0 ? (
          <table className="w-full">
            <thead className="bg-[#1a1a1a]">
              <tr>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Client</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Company</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Status</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Joined</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#262626]">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-[#1a1a1a]">
                  <td className="p-4">
                    <p className="text-white font-medium">{client.firstName} {client.lastName}</p>
                    <p className="text-neutral-500 text-sm">{client.email}</p>
                  </td>
                  <td className="p-4 text-neutral-400">{client.companyName || 'N/A'}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      client.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                      client.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {client.status}
                    </span>
                  </td>
                  <td className="p-4 text-neutral-500 text-sm">{new Date(client.createdAt).toLocaleDateString()}</td>
                  <td className="p-4">
                    <div className="flex gap-2 flex-wrap">
                      {client.status === 'pending' && (
                        <button onClick={() => handleApprove(client.id)} className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20">
                          Approve
                        </button>
                      )}
                      {client.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleChangePlan(client)}
                            className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20"
                          >
                            Change Plan
                          </button>
                          <button onClick={() => handleSuspend(client.id)} className="px-3 py-1 bg-red-500/10 text-red-400 rounded-lg text-xs hover:bg-red-500/20">
                            Suspend
                          </button>
                        </>
                      )}
                      {client.status === 'suspended' && (
                        <button
                          onClick={() => handleChangePlan(client)}
                          className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20"
                        >
                          Assign Plan
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-neutral-500">No clients found</div>
        )}
      </div>

      {/* Change Plan Modal */}
      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => {
          setShowChangePlanModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onSuccess={loadClients}
      />
    </div>
  );
};

export default AdminClients;
